import React, { useState, useEffect } from 'react';
import {
  GraduationCap,
  Calendar,
  DollarSign,
  Users,
  BookOpen,
  FileText,
  Award,
  Target,
  CheckCircle,
  Mail,
  ArrowRight,
  Loader2,
  Clock,
  CreditCard,
  Building2,
  TrendingUp,
  Globe
} from 'lucide-react';

const Seminarios = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [seminarioRes, generalesRes] = await Promise.all([
          fetch(`/data/seminarios.json?v=${Date.now()}`),
          fetch(`/data/generales.json?v=${Date.now()}`)
        ]);
        if (!seminarioRes.ok) throw new Error('Error al cargar los datos del seminario');
        if (!generalesRes.ok) throw new Error('Error al cargar los datos generales');
        const jsonData = await seminarioRes.json();
        const generalesData = await generalesRes.json();
        setData(jsonData);
        setDatosBancarios(generalesData.datosBancarios);
        setError(null);
      } catch (err) {
        console.error('Error cargando datos de seminarios:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Datos estáticos del contenido
  const destinatarios = [
    'Personal docente de ámbito universitario',
    'Estudiantes de programas de postgrado',
    'Investigadores en formación',
    'Profesionales de la educación'
  ];

  const incluye = [
    'Material didáctico digital',
    'Acceso a plataforma educativa',
    'Asesorías personalizadas',
    'Certificado de participación',
    'Acceso a biblioteca digital'
  ];

  const unidades = [
    {
      numero: 1,
      titulo: 'Fundamentos de la Investigación en Ciencias Sociales',
      objetivo: 'Comprender los principios básicos de la investigación científica en el campo de las ciencias sociales',
      subtemas: [
        'El método científico en ciencias sociales',
        'Paradigmas de investigación',
        'Ética en la investigación',
        'El proceso de investigación'
      ]
    },
    {
      numero: 2,
      titulo: 'Diseño de Proyectos de Investigación',
      objetivo: 'Desarrollar habilidades para diseñar proyectos de investigación rigurosos y factibles',
      subtemas: [
        'Planteamiento del problema',
        'Objetivos e hipótesis',
        'Marco teórico',
        'Metodología de investigación'
      ]
    },
    {
      numero: 3,
      titulo: 'Metodologías de Investigación',
      objetivo: 'Dominar diferentes enfoques metodológicos para la investigación social',
      subtemas: [
        'Métodos cualitativos',
        'Métodos cuantitativos',
        'Métodos mixtos',
        'Técnicas de recolección de datos'
      ]
    },
    {
      numero: 4,
      titulo: 'Análisis de Datos y Resultados',
      objetivo: 'Desarrollar competencias para el análisis e interpretación de datos de investigación',
      subtemas: [
        'Análisis estadístico básico',
        'Análisis cualitativo',
        'Interpretación de resultados',
        'Uso de software especializado'
      ]
    },
    {
      numero: 5,
      titulo: 'Redacción Académica y Publicación',
      objetivo: 'Adquirir habilidades para la escritura y publicación de textos científicos',
      subtemas: [
        'Estructura de artículos científicos',
        'Estilos de citación (APA, MLA)',
        'Proceso editorial',
        'Estrategias de publicación'
      ]
    },
    {
      numero: 6,
      titulo: 'Inteligencia Artificial en la Investigación',
      objetivo: 'Integrar herramientas de IA como apoyo en el proceso de investigación y escritura',
      subtemas: [
        'IA para revisión bibliográfica',
        'Herramientas de análisis asistido',
        'IA en la redacción académica',
        'Ética del uso de IA en investigación'
      ]
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando información del seminario...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error: {error || 'No se pudieron cargar los datos'}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#0b742f] text-white px-6 py-2 rounded-lg hover:bg-[#085a24]"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#0b742f] via-[#085a24] to-[#064519] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <GraduationCap className="w-5 h-5" />
              <span className="text-sm font-semibold">Programa de Formación Académica</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Seminario de Producción de Textos Académicos
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mb-6">
              Desarrolla competencias profesionales para escribir y publicar artículos científicos de alta calidad con el apoyo de inteligencia artificial
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Quick Info Cards */}
        <div className="grid md:grid-cols-4 gap-6 -mt-12 mb-16 relative z-10">
          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b742f] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <Calendar className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm">Inscripciones</h3>
            </div>
            <p className="text-lg font-bold text-[#0b742f]">{data.inscripciones.periodo}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#e87729] hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#e87729]/10 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-[#e87729]" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm">Duración</h3>
            </div>
            <p className="text-lg font-bold text-[#e87729]">8 semanas</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-blue-600 hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-600/10 p-3 rounded-lg">
                <Globe className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm">Modalidad</h3>
            </div>
            <p className="text-lg font-bold text-blue-600">100% en línea</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-green-600 hover:scale-105 transition-transform duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-green-600/10 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 text-sm">Inversión</h3>
            </div>
            <p className="text-lg font-bold text-green-600">{data.costo}</p>
          </div>
        </div>

        {/* Estadísticas */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold mb-3">Nuestro Impacto</h2>
              <p className="text-purple-100 text-lg">Más de dos décadas formando investigadores de excelencia</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
                <Users className="w-12 h-12 mx-auto mb-4 text-purple-200" />
                <p className="text-5xl font-bold mb-2">{data.estadisticas.graduados}</p>
                <p className="text-purple-100 text-lg">Graduados</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
                <Award className="w-12 h-12 mx-auto mb-4 text-purple-200" />
                <p className="text-5xl font-bold mb-2">{data.estadisticas.generaciones}</p>
                <p className="text-purple-100 text-lg">Generaciones</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
                <FileText className="w-12 h-12 mx-auto mb-4 text-purple-200" />
                <p className="text-5xl font-bold mb-2">{data.estadisticas.articulosPublicados}</p>
                <p className="text-purple-100 text-lg">Artículos Publicados</p>
              </div>
            </div>
          </div>
        </section>

        {/* Introducción y Objetivo */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Introducción */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#0b742f] p-3 rounded-lg">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Introducción</h2>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">
                El seminario procura introducir a los estudiantes o investigadores de diferentes instancias de posgrado (Carreras de Especialización, Maestrías y/o Doctorados) en la lógica de la investigación académica en ciencias sociales, poniendo énfasis en el abordaje de preguntas sobre lo social que requieran ser respondidas a través de procedimientos científicamente fundados.
              </p>
            </div>

            {/* Objetivo General */}
            <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <Target className="w-8 h-8" />
                <h2 className="text-3xl font-bold">Objetivo General</h2>
              </div>
              <p className="text-lg leading-relaxed text-orange-50">
                Desarrollar competencias profesionales necesarias para escribir y publicar artículos científicos de alta calidad, utilizando la inteligencia artificial como apoyo para optimizar el proceso y potenciar la calidad del trabajo.
              </p>
            </div>
          </div>
        </section>

        {/* Destinatarios */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-blue-600">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-blue-600 p-3 rounded-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Destinado a</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {destinatarios.map((dest, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-5 border border-blue-100 hover:border-blue-400 group">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 group-hover:bg-blue-600 p-2 rounded-lg transition-colors duration-300">
                      <CheckCircle className="w-6 h-6 text-blue-600 group-hover:text-white" />
                    </div>
                    <span className="text-gray-900 font-semibold">{dest}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Temario */}
        <section id="temario" className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="bg-[#0b742f] p-3 rounded-lg">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-4xl font-bold text-gray-900">Temario del Seminario</h2>
              </div>
              <div className="h-1 w-32 bg-gradient-to-r from-[#0b742f] to-[#e87729] rounded mx-auto"></div>
            </div>

            <div className="space-y-6">
              {unidades.map((unidad, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-6 hover:border-[#0b742f] hover:shadow-2xl transition-all duration-300 group"
                >
                  <div className="flex items-start gap-5">
                    <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-2xl w-16 h-16 flex items-center justify-center flex-shrink-0 text-3xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                      {unidad.numero}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#0b742f] transition-colors duration-300">
                        {unidad.titulo}
                      </h3>
                      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4 mb-4">
                        <p className="text-gray-700 italic">
                          <strong className="text-blue-700">Objetivo:</strong> {unidad.objetivo}
                        </p>
                      </div>
                      <div className="bg-gradient-to-r from-gray-50 to-green-50 rounded-xl p-5 border border-gray-200">
                        <p className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <FileText className="w-5 h-5 text-[#0b742f]" />
                          Contenido temático:
                        </p>
                        <ul className="grid md:grid-cols-2 gap-3">
                          {unidad.subtemas.map((subtema, idx) => (
                            <li key={idx} className="flex items-start gap-2 bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow duration-200">
                              <ArrowRight className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 font-medium text-sm">{subtema}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Costos e Inscripción */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-green-600 p-3 rounded-lg">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Fechas y Costos de Inscripción</h2>
            </div>

            {/* Calendarios de Inscripción */}
            <div className="mb-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
                  <Calendar className="w-7 h-7 text-[#0b742f]" />
                  Calendarios de Inscripción
                </h3>
                <div className="h-1 w-24 bg-gradient-to-r from-[#0b742f] to-[#e87729] rounded mx-auto mb-3"></div>
                <p className="text-gray-600">Elige el periodo que mejor se ajuste a tus necesidades</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {data.calendarios && data.calendarios.map((calendario) => (
                  <div
                    key={calendario.id}
                    className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#0b742f] hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="text-center mb-3">
                      <div className="bg-gradient-to-br from-[#0b742f] to-[#085a24] text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 text-xl font-bold shadow-md group-hover:scale-110 transition-transform duration-300">
                        {calendario.id}
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 mb-3">Periodo {calendario.id}</h4>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-500">
                        <p className="text-xs font-semibold text-blue-700 mb-1 uppercase">Inscripciones</p>
                        <p className="text-xs font-bold text-gray-900">{calendario.periodoInscripcion}</p>
                      </div>

                      <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-500">
                        <p className="text-xs font-semibold text-green-700 mb-1 uppercase">Inicio</p>
                        <p className="text-xs font-bold text-gray-900">{calendario.inicioSeminario}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-r-xl p-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      <strong className="text-gray-900">Nota:</strong> Puedes inscribirte en cualquiera de los periodos disponibles. El seminario tiene una duración de 8 semanas y se imparte 100% en línea en modalidad asincrónica.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Información de Inversión */}
              <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="w-10 h-10" />
                  <div>
                    <h3 className="text-2xl font-bold">Inversión</h3>
                    <p className="text-green-100 text-sm">Periodo: {data.inscripciones.periodo}</p>
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 mb-6">
                  <p className="text-green-100 text-sm mb-2">Costo del Seminario</p>
                  <p className="text-5xl font-bold mb-2">{data.costo}</p>
                  <p className="text-green-100">Pago único • MXN</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-50">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm">Material incluido</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-50">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm">Certificado al finalizar</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-50">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm">Acceso a plataforma</span>
                  </div>
                </div>
              </div>

              {/* Lo que incluye */}
              <div className="bg-white border-2 border-green-200 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Award className="w-7 h-7 text-green-600" />
                  La inscripción incluye
                </h3>
                <ul className="space-y-3">
                  {incluye.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 bg-green-50 p-4 rounded-lg hover:bg-green-100 transition-colors duration-200">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-800 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Información de Pago */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 rounded-r-2xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-4 text-xl flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
                Información de Pago
              </h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">1</span>
                  <span>Los pagos deberán efectuarse vía banca electrónica</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">2</span>
                  <span>Pagos desde el extranjero: PayPal o Western Union (agregar 5% por comisión)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">3</span>
                  <span>Todos los pagos deberán ser reportados al correo: <a href={`mailto:${data.contacto}`} className="text-green-600 font-bold hover:underline break-all">{data.contacto}</a></span>
                </li>
              </ul>
            </div>

            {/* Datos Bancarios */}
            {datosBancarios && (
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8 border-t-4 border-gray-700">
                <h4 className="font-bold text-gray-900 mb-6 text-2xl flex items-center gap-3">
                  <Building2 className="w-7 h-7 text-gray-700" />
                  Datos Bancarios
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">Titular</p>
                    <p className="font-bold text-gray-900">{datosBancarios.titular}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">Banco</p>
                    <p className="font-bold text-gray-900">{datosBancarios.banco}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">Número de Cuenta</p>
                    <p className="font-bold text-gray-900 text-lg">{datosBancarios.numeroCuenta}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">CLABE Interbancaria</p>
                    <p className="font-bold text-gray-900 text-lg">{datosBancarios.clabe}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">SWIFT</p>
                    <p className="font-bold text-gray-900">{datosBancarios.swift}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">Sucursal</p>
                    <p className="font-bold text-gray-900 text-sm">{datosBancarios.sucursal}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Metodología y Evaluación */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-purple-600">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-purple-600 p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Metodología y Evaluación</h2>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-md border border-purple-100">
                <h3 className="text-xl font-bold text-purple-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-6 h-6" />
                  Enfoque Pedagógico
                </h3>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  La metodología de trabajo será de carácter básicamente práctico con el enfoque del "aprender haciendo". El alumnado aprenderá a producir textos académicos haciéndolos. En el aula virtual se darán orientaciones y pautas para el aprendizaje de los contenidos y la realización de actividades.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Las actividades serán de carácter tanto individual como colaborativo y estarán tutorizadas en todo momento por los asesores.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-2xl font-bold mb-4">Evaluación Continua</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-6xl font-bold">30%</div>
                    <p className="text-purple-100">Actividades desarrolladas durante el seminario</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-pink-600 to-pink-700 text-white rounded-xl p-6 shadow-lg">
                  <h3 className="text-2xl font-bold mb-4">Evaluación Final</h3>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-6xl font-bold">70%</div>
                    <p className="text-pink-100">Presentación de un texto académico específico</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border-2 border-indigo-200 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-xl font-bold text-indigo-900 mb-4 flex items-center gap-2">
                    <Users className="w-6 h-6 text-indigo-600" />
                    Encuentros Sincrónicos
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      Asesoría virtual semanal
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      Sesiones vía Zoom
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600" />
                      Retroalimentación personalizada
                    </li>
                  </ul>
                </div>

                <div className="bg-white border-2 border-teal-200 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-xl font-bold text-teal-900 mb-4 flex items-center gap-2">
                    <Globe className="w-6 h-6 text-teal-600" />
                    Modalidad Asincrónica
                  </h3>
                  <div className="bg-teal-50 rounded-lg p-4">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Las clases no están publicadas en tiempo real, es decir, el alumno no debe conectarse en un día u horario predeterminado. Las clases permanecen accesibles para los alumnos desde su fecha de publicación.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contacto CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#0b742f] via-[#085a24] to-[#064519] rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-8 md:p-12 text-center relative">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnptMCAyYy0yLjIxIDAtNCAxLjc5LTQgNHMxLjc5IDQgNCA0IDQtMS43OSA0LTQtMS43OS00LTQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50"></div>
              <div className="relative z-10">
                <div className="bg-white/10 backdrop-blur-sm rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  ¿Listo para comenzar?
                </h2>
                <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
                  Contáctanos para obtener más información o resolver cualquier duda sobre el seminario
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a
                    href={`mailto:${data.contacto}`}
                    className="inline-flex items-center gap-3 bg-white text-[#0b742f] px-10 py-5 rounded-xl font-bold text-xl hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:scale-105 break-all"
                  >
                    <Mail className="w-7 h-7 flex-shrink-0" />
                    <span className="break-all">{data.contacto}</span>
                  </a>
                  <a
                    href="#"
                    className="inline-flex items-center gap-3 bg-[#e87729] text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-[#d96820] transition-all duration-300 shadow-2xl hover:scale-105"
                  >
                    <ArrowRight className="w-7 h-7" />
                    Inscríbete Ahora
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Seminarios;
